import React, { useState, useEffect } from 'react';
import { Container } from '../common';
import {
  BarChart3,
  Bot,
  ChefHat,
  ChevronDown,
  Gift,
  MonitorSmartphone,
  PackageCheck,
  Percent,
  PlugZap,
  RotateCcw,
  ShoppingCart,
  Smartphone,
  Star,
  Truck,
  UserX,
  Users,
  UtensilsCrossed
} from 'lucide-react';

const featureMenuItems = [
  {
    icon: Smartphone,
    title: 'Cardápio digital',
    description: 'Venda por link, domínio próprio, slug ou QR Code com uma jornada mobile-first.',
    href: '#funcionalidades'
  },
  {
    icon: ShoppingCart,
    title: 'Pedidos e checkout',
    description: 'Carrinho, cupons, retirada, entrega, agendamento e validação do pedido.',
    href: '#funcionalidades'
  },
  {
    icon: Gift,
    title: 'Cashback',
    description: 'Relacione clientes com saldo, extrato e incentivo real para recompra.',
    href: '#funcionalidades'
  },
  {
    icon: Users,
    title: 'Gestão de clientes',
    description: 'Base de clientes com histórico, contatos, ticket médio e comportamento de compra.',
    href: '#funcionalidades'
  },
  {
    icon: UserX,
    title: 'Bloqueio de clientes',
    description: 'Bloqueie e desbloqueie clientes com motivo, registro e histórico de ações.',
    href: '#funcionalidades'
  },
  {
    icon: Star,
    title: 'Avaliações',
    description: 'Notas, comentários, média do restaurante e alertas para feedbacks críticos.',
    href: '#funcionalidades'
  },
  {
    icon: RotateCcw,
    title: 'Recuperador de vendas',
    description: 'Reative carrinhos abandonados e clientes inativos com follow-up controlado.',
    href: '#funcionalidades'
  },
  {
    icon: Percent,
    title: 'Promoções e combos',
    description: 'Cupons, ofertas, combos e campanhas para aumentar conversão e ticket médio.',
    href: '#funcionalidades'
  },
  {
    icon: ChefHat,
    title: 'Ficha técnica',
    description: 'Receitas, insumos, custos e padronização da produção no cardápio.',
    href: '#funcionalidades'
  },
  {
    icon: PackageCheck,
    title: 'Estoque e custos',
    description: 'Controle ingredientes, fornecedores, movimentações e impacto no produto.',
    href: '#funcionalidades'
  },
  {
    icon: Bot,
    title: 'WhatsApp com IA',
    description: 'Evolution API, N8N, automações, memória de conversa e atendimento humano.',
    href: '#funcionalidades'
  },
  {
    icon: PlugZap,
    title: 'Integrações',
    description: 'iFood, APIs, webhooks, SQS/SNS, Google Maps, Firebase e gateways.',
    href: '#funcionalidades'
  },
  {
    icon: MonitorSmartphone,
    title: 'Portal, POS e KDS',
    description: 'Gestão, operação de loja, impressão ESC/POS e produção em cozinha.',
    href: '#funcionalidades'
  },
  {
    icon: Truck,
    title: 'Entregas',
    description: 'Taxas, áreas, retirada, agendamento e acompanhamento operacional.',
    href: '#funcionalidades'
  },
  {
    icon: BarChart3,
    title: 'Dashboard e financeiro',
    description: 'Indicadores, contas, fluxo, taxas, folha e visão de operação.',
    href: '#funcionalidades'
  },
  {
    icon: UtensilsCrossed,
    title: 'Restaurantes e deliverys',
    description: 'Hamburguerias, pizzarias, lanchonetes, açaís, marmitarias e mais.',
    href: '#segmentos'
  }
];

const Header = () => {
  const [activeItem, setActiveItem] = useState('inicio');
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const isHome = window.location.pathname === '/';
  const homeHref = (sectionId) => isHome ? `#${sectionId}` : `/#${sectionId}`;

  const closeMenu = () => {
    setFeaturesOpen(false);
    const navbar = document.getElementById('navbarNav');
    if (navbar && navbar.classList.contains('show')) {
      const bsCollapse = new window.bootstrap.Collapse(navbar);
      bsCollapse.hide();
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['inicio', 'problema', 'funcionalidades', 'como-funciona', 'planos', 'contato', 'faq'];
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveItem(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="sticky-top bg-white border-bottom border-custom py-2 py-lg-3 shadow-sm">
      <Container>
        <nav className="navbar navbar-expand-lg navbar-light p-0">
          <a className="navbar-brand d-flex align-items-center" href="/">
            <img src="/logo-leo-delivery-minimalist.png" alt="Léo Delivery Logo" style={{ height: '40px', width: 'auto' }} className="me-2 rounded-2" />
            <span className="fw-bold h4 mb-0">Léo Delivery</span>
          </a>
          
          <button 
            className="navbar-toggler" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav mx-auto">
              <li className="nav-item">
                <a 
                  className={`nav-link px-3 fw-medium ${activeItem === 'inicio' ? 'text-primary-custom active-nav' : 'text-dark'}`} 
                  href={homeHref('inicio')}
                  onClick={closeMenu}
                >
                  Início
                </a>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link px-3 fw-medium ${activeItem === 'problema' ? 'text-primary-custom active-nav' : 'text-dark'}`} 
                  href={homeHref('problema')}
                  onClick={closeMenu}
                >
                  Problema
                </a>
              </li>
              <li
                className="nav-item features-menu"
                onMouseEnter={() => setFeaturesOpen(true)}
                onMouseLeave={() => setFeaturesOpen(false)}
              >
                <button
                  className={`nav-link px-3 fw-medium features-menu__trigger ${activeItem === 'funcionalidades' ? 'text-primary-custom active-nav' : 'text-dark'}`}
                  type="button"
                  aria-expanded={featuresOpen}
                  aria-controls="featuresMegaMenu"
                  onClick={() => setFeaturesOpen((current) => !current)}
                >
                  Funcionalidades
                  <ChevronDown size={16} className={`features-menu__chevron ${featuresOpen ? 'is-open' : ''}`} />
                </button>
                <div
                  id="featuresMegaMenu"
                  className={`features-mega-menu ${featuresOpen ? 'is-open' : ''}`}
                >
                  <div className="features-mega-menu__grid">
                    {featureMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <a
                          key={item.title}
                          href={isHome ? item.href : `/${item.href}`}
                          className="features-mega-menu__item"
                          onClick={closeMenu}
                        >
                          <span className="features-mega-menu__icon">
                            <Icon size={22} />
                          </span>
                          <span>
                            <strong>{item.title}</strong>
                            <small>{item.description}</small>
                          </span>
                        </a>
                      );
                    })}
                  </div>
                  <div className="features-mega-menu__footer">
                    <span>Plataforma completa para vender, operar e crescer com menos dependência de marketplaces.</span>
                    <a href={homeHref('contato')} onClick={closeMenu}>Ver demonstração</a>
                  </div>
                </div>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link px-3 fw-medium ${activeItem === 'como-funciona' ? 'text-primary-custom active-nav' : 'text-dark'}`} 
                  href={homeHref('como-funciona')}
                  onClick={closeMenu}
                >
                  Como funciona
                </a>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link px-3 fw-medium ${activeItem === 'planos' ? 'text-primary-custom active-nav' : 'text-dark'}`} 
                  href={homeHref('planos')}
                  onClick={closeMenu}
                >
                  Planos
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link px-3 fw-medium text-dark"
                  href="/blog"
                  onClick={closeMenu}
                >
                  Blog
                </a>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link px-3 fw-medium ${activeItem === 'contato' ? 'text-primary-custom active-nav' : 'text-dark'}`} 
                  href={homeHref('contato')}
                  onClick={closeMenu}
                >
                  Contato
                </a>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link px-3 fw-medium ${activeItem === 'faq' ? 'text-primary-custom active-nav' : 'text-dark'}`} 
                  href={homeHref('faq')}
                  onClick={closeMenu}
                >
                  FAQ
                </a>
              </li>
            </ul>
            <div className="d-lg-none mt-3">
              <a href={homeHref('contato')} className="btn btn-primary-custom w-100" onClick={closeMenu}>Falar com consultor</a>
            </div>
            <a href={homeHref('contato')} className="btn btn-primary-custom d-none d-lg-block">Falar com consultor</a>
          </div>
        </nav>
      </Container>
    </header>
  );
};

export default Header;
